import { Icon } from '@chakra-ui/react'
import { Check, Phone, TriangleAlert } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useRef } from 'react'
import styled from 'styled-components'
import { useToastState } from '~/atoms/ToastState'
import StatusToast from '~/components/StatusToast'
import { getDropZoneMapping } from '~/constants/config'

export enum MessagingToastStatus {
  General = 'general',
  ClickToCall = 'click-to-call',
  ErrorClickToCall = 'error-click-to-call',
  DropZoneError = 'dropZoneError',
  ErrorSendMessage = 'error-send-message',
  RenameThread = 'rename-thread',
  ErrorRenameThread = 'error-rename-thread',
  FileInvalidType = 'file-invalid-type',
  FileTooLarge = 'file-too-large',
}

const MessagingToastWrapper = styled.div`
  position: fixed;
  right: 0px;
  top: -10px;
  width: 350px;
  height: 35px;
  z-index: 100;
`

//TODO: Revising this TODO - the way we handle Toasts is absolutely ridiculous and should be event-based - this system needs a big revision to handle things this way moving forward.
const MessagingToast = () => {
  const {
    messagingToast: [messagingToastState],
  } = useToastState()

  const {
    messagingStatus,
    messagingError,
    clickToCallStatus,
    threadUpdateStatus,
  } = messagingToastState

  const [shouldRenderToast, setShouldRenderToast] = useState(false)
  const [currentStatus, setCurrentStatus] = useState<string | null>(null)
  const toastTimer = useRef(0)
  const toastTimerDurationMs = 3500

  const dropZoneMapping =
    currentStatus === MessagingToastStatus.DropZoneError
      ? getDropZoneMapping(messagingStatus, 'chat')
      : null

  function getCurrentStatus() {
    if (clickToCallStatus === MessagingToastStatus.ClickToCall) {
      return 'ring'
    }
    if (threadUpdateStatus === MessagingToastStatus.RenameThread) {
      return 'renameThread'
    }
    if (
      messagingStatus === MessagingToastStatus.FileInvalidType ||
      messagingStatus === MessagingToastStatus.FileTooLarge ||
      messagingStatus === MessagingToastStatus.General
    ) {
      return 'dropZoneError'
    }
    if (messagingStatus === MessagingToastStatus.ErrorSendMessage) {
      return 'sendMessageError'
    }

    return null
  }

  const clearToastStatus = () => {
    setShouldRenderToast(false)
    setCurrentStatus(null)
    toastTimer.current = 0
  }

  const handleCancelToast = () => {
    clearToastStatus()
  }

  const tryDecrementToastTimer = (repeatValue: number) => {
    if (toastTimer.current > 0) {
      toastTimer.current = Math.max(0, toastTimer.current - repeatValue)
      setTimeout(() => tryDecrementToastTimer(repeatValue), repeatValue)
      return
    }

    //toast is done rendering, hide it
    handleCancelToast()
  }

  const startToastTimer = () => {
    if (toastTimer.current > 0) {
      //toast timer is already running, set timer back to full duration, but do nothing else;
      toastTimer.current = toastTimerDurationMs
      return
    }

    toastTimer.current = toastTimerDurationMs
    const oneThirdToastTimer = toastTimerDurationMs / 3
    //toast timer is not running, start it
    setTimeout(
      () => tryDecrementToastTimer(oneThirdToastTimer),
      oneThirdToastTimer,
    )
  }

  //watches for state updates and sets the current status
  useEffect(() => {
    const status = getCurrentStatus()
    if (status) {
      setShouldRenderToast(true)
      startToastTimer()
      setCurrentStatus(status)
    }
  }, [
    messagingToastState,
    clickToCallStatus,
    messagingStatus,
    messagingError,
    threadUpdateStatus,
  ])

  const iconWarning = (
    <Icon boxSize="17px" color="red.solid">
      <TriangleAlert size="100%" />
    </Icon>
  )
  const iconPhone = (
    <Icon boxSize="17px" color="brand.solid">
      <Phone size="100%" />
    </Icon>
  )
  const iconCheck = (
    <Icon boxSize="17px" color="green.solid">
      <Check size="100%" />
    </Icon>
  )

  const composeToastConfig = () => {
    const isError =
      clickToCallStatus === 'error-click-to-call' ||
      threadUpdateStatus === 'error-rename-thread' ||
      currentStatus === 'dropZoneError' ||
      currentStatus === 'sendMessageError'

    switch (currentStatus) {
      case 'ring':
        return {
          title: isError ? 'Failed to Call' : 'Calling your Deskphone',
          message: isError
            ? 'Unable to process this click to call'
            : `Starting click to call...`,
          icon: isError ? iconWarning : iconPhone,
          error: isError,
        }
      case 'dropZoneError':
        return {
          title: dropZoneMapping?.title,
          message: dropZoneMapping?.description,
          icon: iconWarning,
          error: true,
        }
      case 'renameThread':
        return {
          title: isError ? 'Error' : 'Success',
          message: isError
            ? 'Unable to process your request. Please try again later.'
            : 'Text name was changed successfully.',
          icon: isError ? iconWarning : iconCheck,
          error: isError,
        }
      case 'sendMessageError':
        return {
          title: 'Message Failed to Send',
          message: messagingError
            ? messagingError
            : 'Verify the destination number and message body. For MMS, verify your file is < 1MB, and a supported file type. For additional help visit https://support.mangovoice.com/web-app#web-app-texting-tab',
          icon: iconWarning,
          error: true,
        }
      default:
        return null
    }
  }

  const toastConfig = composeToastConfig()
  if (!shouldRenderToast || !toastConfig) return null

  return (
    <MessagingToastWrapper>
      <StatusToast
        title={String(toastConfig.title)}
        message={String(toastConfig.message)}
        icon={toastConfig.icon}
        onClose={handleCancelToast}
        error={toastConfig.error}
      />
    </MessagingToastWrapper>
  )
}

export default MessagingToast
